import type { FiscalInvoiceInput, FiscalReceiptInput, FiscalVatRate } from '@ip/shared-types';

/**
 * VAT percent per rate code — mirrors the backend's `RATES` table in
 * `apps/backend/src/modules/fiscal/fa3-builder.ts` (`computeTotals`). Only
 * '23'/'8'/'5' carry a non-zero percentage; the rest (0%, WDT/EX exports,
 * zw/np/oo exemptions) contribute net-only.
 */
const VAT_PERCENT: Record<FiscalVatRate, number> = {
  '23': 23,
  '8': 8,
  '5': 5,
  '0': 0,
  '0_WDT': 0,
  '0_EX': 0,
  zw: 0,
  np: 0,
  oo: 0,
};

/**
 * Gross total (in the invoice's own currency) for a fiscal invoice payload,
 * grouped by VAT rate and rounded in grosze per group — same algorithm as
 * the backend's FA(3) builder, so the displayed figure matches what actually
 * gets fiscalized. Client-side only (list/detail views); never sent anywhere.
 */
export function computeFiscalInvoiceGross(invoice: FiscalInvoiceInput): number {
  const netGrByRate = new Map<FiscalVatRate, number>();
  let grossItemsGr = 0;
  for (const item of invoice.items) {
    if (item.unitPriceGross !== undefined) {
      // Pozycja brutto (P_9B/P_11A): jej wkład do sumy to dokładnie wartość
      // brutto pozycji — bez odtwarzania z zaokrąglonego netto (parytet z
      // backendowym computeTotals, metoda "w stu" per pozycja).
      grossItemsGr += Math.round(item.quantity * item.unitPriceGross * 100);
      continue;
    }
    const netGr = Math.round(item.quantity * (item.unitPriceNet ?? 0) * 100);
    netGrByRate.set(item.vatRate, (netGrByRate.get(item.vatRate) ?? 0) + netGr);
  }
  let totalGr = grossItemsGr;
  for (const [rate, netGr] of netGrByRate) {
    const vatGr = Math.round((netGr * VAT_PERCENT[rate]) / 100);
    totalGr += netGr + vatGr;
  }
  return totalGr / 100;
}

/**
 * Gross total (always PLN) for a fiscal receipt payload — item unit prices
 * are already gross (Polish receipt convention), so this is a plain sum,
 * mirroring `receipt-pdf.ts`'s `totalGr` accumulation.
 */
export function computeFiscalReceiptTotal(receipt: FiscalReceiptInput): number {
  let totalGr = 0;
  for (const item of receipt.items) {
    totalGr += Math.round(item.quantity * item.unitPrice * 100);
  }
  return totalGr / 100;
}
